/**
 * Translation dictionary for the public job application page (/apply).
 *
 * Scope: this file only covers PublicApplicationForm.js. The authenticated
 * admin screens (Dashboard, Positions, ActiveList, etc.) are not translated -
 * the request that created this file was specifically about applicants (who
 * do not log in) finding the apply page hard to use in English.
 *
 * Structure: flat dot-path keys inside each language object, e.g.
 * t('errors.fields.phoneRequired'). This mirrors how resource bundles work
 * in react-i18next on purpose - see LanguageContext.js for why - so if this
 * ever needs to grow into that library, these objects can become
 * en.json/es.json/fr.json/pt.json almost unchanged, and most call sites
 * (`t('some.key')`) would not need to change at all.
 *
 * IMPORTANT: es/fr/pt text below was drafted by an engineer, not a native
 * speaker or certified translator, and has NOT been reviewed by legal. This
 * is especially true of the `disclaimer` section, which is a legal
 * certification and privacy notice. Per instruction, this is a first draft
 * to be reviewed/corrected by the manager/legal before these languages are
 * relied upon in production.
 *
 * Deliberately NOT translated (same literal value in every language):
 *  - Radio/checkbox "value" attributes (e.g. formData.employment_type is
 *    always stored as the English string "Full time", never a translated
 *    one) - the admin dashboard and database filter/compare on these exact
 *    English strings today. Only the on-screen *label* next to each option
 *    is translated (see OPTION keys in PublicApplicationForm.js).
 *  - US state abbreviations (AL, AK, ...) - these are codes, not words.
 *  - The privacy-policy/terms-of-service URLs and the contact email address.
 */

export const translations = {
  // ---------------------------------------------------------------------
  // ENGLISH (source of truth / fallback language)
  // ---------------------------------------------------------------------
  en: {
    languageSwitcher: {
      label: 'Language'
    },

    header: {
      title: 'Join our Team!',
      needHelp: 'Help?',
      callUs: '{{phone}}'
    },

    steps: {
      position: 'Position',
      contact: 'Contact',
      general: 'General',
      schedule: 'Schedule',
      references: 'References',
      review: 'Review'
    },

    nav: {
      previous: '← Previous',
      next: 'Next →',
      submit: 'Submit Application',
      submitting: 'Submitting...'
    },

    loading: 'Loading available positions...',

    noPositions: 'There are currently no open positions. Please check back later!',

    success: {
      title: 'Application Submitted Successfully!',
      body: 'Thank you for your interest. We will review your application and contact you soon.',
      button: 'Submit Another Application'
    },

    common: {
      yes: 'Yes',
      no: 'No',
      optional: 'optional'
    },

    position: {
      title: 'Choose Your Position',
      subtitle: "Browse available positions and select the one you'd like to apply for",
      searchPlaceholder: 'Search positions by title or location...',
      selected: '✓ Selected',
      viewDescription: '📄 View Description',
      noResults: 'No positions found matching "{{search}}"',
      selectionPrompt: '👆 Please select a position above to continue',
      previousPage: '← Previous',
      nextPage: 'Next →',
      pageInfo: 'Page {{page}} of {{totalPages}} ({{count}} positions)',
      additionalInfo: 'Additional Information',
      howDidYouHear: 'How did you hear about us?',
      closeModal: 'Close'
    },

    contact: {
      title: 'Contact Information',
      sectionTitle: 'Your Details',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      phoneCountryLabel: 'Phone country',
      streetAddress: 'Street Address',
      streetAddress2: 'Street Address 2',
      streetAddress2Placeholder: 'Apt, Suite, Unit, etc.',
      city: 'City',
      state: 'State',
      selectState: 'Select State',
      zip: 'ZIP Code',
      country: 'Country'
    },

    general: {
      title: 'General Questions',
      workedBefore: 'Have you worked for this company before?',
      yearsExperience: 'How many years experience do you have in the position for which you are interested?',
      atLeast18: 'Are you at least 18 years of age?',
      under18Notice: 'Please note: applicants under 18 may still apply. If selected, you will need to provide written authorization from a parent or legal guardian before you can begin work, and additional documentation may be required.',
      hasFlLicense: 'Have you got a Florida issued driver license or ID?',
      felonyConviction: 'Have you been convicted of a felony or been incarcerated in connection with a felony in the past seven years?',
      militaryService: 'Military Service?',
      educationLevel: 'Highest level of Education'
    },

    schedule: {
      title: 'Schedule',
      employmentType: 'Would you prefer to work full/part time or temporary?',
      shiftPreference: 'Would you prefer to work days, afternoons or nights?',
      availableDays: 'Select which days of the week you are available to work',
      changingSchedule: 'Are you willing to work a changing schedule?',
      startDate: 'What is the earliest date you can start?'
    },

    references: {
      title: 'References',
      hasReferences: 'Do you have any references?',
      referenceNumber: 'Reference {{index}}',
      remove: 'Remove',
      name: 'Name',
      phoneNumber: 'Phone Number',
      email: 'Email',
      relationship: 'Relationship',
      relationshipPlaceholder: 'e.g., Former Supervisor',
      addAnother: '+ Add Another Reference',
      resumeTitle: 'Resume / CV (optional)',
      resumeDescription: 'You may attach a resume as a PDF file, up to 5 MB.',
      attached: 'Attached:',
      removeFile: 'Remove'
    },

    review: {
      title: 'Review Your Application',
      subtitle: 'Please review your information carefully before submitting.',
      positionSource: 'Position & Source',
      position: 'Position:',
      howYouHeard: 'How you heard about us:',
      contactInformation: 'Contact Information',
      name: 'Name:',
      email: 'Email:',
      phone: 'Phone:',
      address: 'Address:',
      generalQuestions: 'General Questions',
      workedHereBefore: 'Worked here before:',
      yearsOfExperience: 'Years of experience:',
      atLeast18YearsOld: 'At least 18 years old:',
      floridaLicense: 'Florida driver license/ID:',
      felonyConviction: 'Felony conviction (past 7 years):',
      militaryService: 'Military service:',
      highestEducation: 'Highest education:',
      schedulePreferences: 'Schedule Preferences',
      employmentType: 'Employment type:',
      shiftPreference: 'Shift preference:',
      availableDays: 'Available days:',
      changingSchedule: 'Willing to work changing schedule:',
      earliestStartDate: 'Earliest start date:',
      references: 'References',
      noReferences: 'No references provided',
      under18Notice: 'Please note: because you indicated you are under 18, written authorization from a parent or legal guardian will be required before you can begin work if you are selected.'
    },

    disclaimer: {
      title: 'Applicant Certification & Privacy Notice',
      certification: "I certify that the information contained on this application is correct to the best of my knowledge, and that deliberate falsification of this information is grounds for dismissal. I authorize the references to give you any and all information concerning my past employment, personal or otherwise, and release all parties from all liability for any damage that may result from furnishing information to you. I understand that if I become employed, I will be free to terminate my employment at any time for any reason and the company has the same rights. No representative of the company has the authority to make any contrary agreement. No offer of employment is official until I complete the new hire process.",
      privacyNoticeTitle: 'Applicant Privacy Notice',
      privacyIntro: 'At Clean Tec Outsourcing LLC, we are committed to protecting your privacy. Please review the following notice to understand how we collect, use, share, and secure information about you through the career application process.',
      privacyPolicyPrefix: 'For a full description of our privacy practices, see our Privacy Policy at',
      termsPrefix: 'View our Terms of Service at',
      contactPrefix: 'If you have any questions or need HELP, please contact us at'
    },

    options: {
      source: {
        companyWebsite: 'Company Website',
        indeed: 'Indeed',
        linkedin: 'LinkedIn',
        referral: 'Employee Referral',
        jobFair: 'Job Fair',
        other: 'Other'
      },
      yearsExperience: {
        noExperience: 'No Experience',
        lessThanOneYear: 'Less than one year',
        oneToThreeYears: '1 to 3 years',
        fourOrMoreYears: '4 or more years'
      },
      educationLevel: {
        highSchool: 'High School or GED',
        someCollege: 'Some College',
        associates: 'Associates',
        bachelors: 'Bachelors',
        masters: 'Masters'
      },
      employmentType: {
        fullTime: 'Full time',
        partTime: 'Part time',
        temporary: 'Temporary'
      },
      shiftPreference: {
        days: 'Days',
        afternoons: 'Afternoons',
        nights: 'Nights'
      },
      days: {
        all: 'All',
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
        holidays: 'Holidays'
      },
      changingSchedule: {
        yes: 'Yes',
        no: 'No',
        occasionally: 'Occasionally'
      },
      startDate: {
        immediately: 'Immediately',
        oneToTwoWeeks: '1-2 weeks',
        overThreeWeeks: 'Over 3 weeks'
      }
    },

    // Client-side validation messages, shown directly under the offending
    // field (not as a top banner). Keyed by the formData field name.
    errors: {
      loadPositionsFailed: 'Unable to load positions. Please try again later.',
      submitFailed: 'Application submission failed. Please try again.',
      generic: 'Something went wrong. Please try again.',
      pleaseCorrect: 'Missing Required Fields',
      resumeNotPdf: 'Please choose a PDF file.',
      resumeTooLarge: 'That file is larger than 5 MB. Please choose a smaller file.',

      fields: {
        position: 'Please select a position to continue.',
        firstName: 'Please enter your first name.',
        lastName: 'Please enter your last name.',
        emailRequired: 'Please enter your email address.',
        emailInvalid: 'Please enter a valid email address (for example: name@example.com).',
        phoneRequired: 'Please enter your phone number.',
        phoneInvalid: 'Please enter a valid phone number for the selected country.',
        city: 'Please enter your city.',
        state: 'Please select your state.',
        zip: 'Please enter your ZIP code.',
        workedBefore: 'Please let us know if you have worked for this company before.',
        yearsExperience: 'Please select your years of experience.',
        atLeast18: 'Please let us know if you are at least 18 years of age.',
        hasFlLicense: 'Please let us know if you have a Florida issued driver license or ID.',
        felonyConviction: 'Please answer the felony conviction question.',
        militaryService: 'Please let us know about your military service.',
        educationLevel: 'Please select your highest level of education.',
        employmentType: 'Please select full time, part time, or temporary.',
        shiftPreference: 'Please select your shift preference.',
        availableDays: 'Please select at least one day you are available to work.',
        changingSchedule: 'Please let us know if you are willing to work a changing schedule.',
        startDate: 'Please select your earliest start date.',
        hasReferences: 'Please let us know if you have any references.'
      },

      references: {
        incomplete: 'Please provide a name and phone number for each reference.',
        invalidPhone: 'Please enter a valid phone number for each reference.',
        invalidEmail: 'Please enter a valid email address for each reference, or leave it blank.'
      }
    },

    // Maps stable error codes returned by the server to a localized message.
    // Any server code not listed here falls back to whatever text the
    // server sent (English), so nothing silently disappears if a code is
    // missed - see mapServerError() in PublicApplicationForm.js.
    serverErrors: {
      required_position: 'Please select a position to continue.',
      required_first_name: 'Please enter your first name.',
      required_last_name: 'Please enter your last name.',
      invalid_email: 'Please enter a valid email address (for example: name@example.com).',
      required_phone: 'Please enter your phone number.',
      required_phone_country: 'Please select your phone country.',
      invalid_phone_country: 'Please select a valid country for your phone number.',
      invalid_phone_for_country: 'Please enter a valid phone number for the selected country.',
      invalid_references_format: 'References were not formatted correctly.',
      reference_missing_name_or_phone: "Reference {{index}} is missing a name or phone number.",
      invalid_reference_phone_country: "Please select a valid country for reference {{index}}'s phone number.",
      invalid_reference_phone: 'Please enter a valid phone number for reference {{index}}.',
      invalid_reference_email: 'Please enter a valid email address for reference {{index}}, or leave it blank.',
      position_not_available: 'This position is no longer available. Please choose another position.',
      resume_too_large: 'Resume must be 5 MB or smaller.',
      resume_invalid: 'Could not process the uploaded file.',
      too_many_references: 'No more than {{max}} references are allowed.'
    }
  },

  // ---------------------------------------------------------------------
  // SPANISH (draft - not yet reviewed)
  // ---------------------------------------------------------------------
  es: {
    languageSwitcher: {
      label: 'Idioma'
    },

    header: {
      title: '¡Únase a Nuestro Equipo!',
      needHelp: '¿Ayuda?',
      callUs: '{{phone}}'
    },

    steps: {
      position: 'Puesto',
      contact: 'Contacto',
      general: 'General',
      schedule: 'Horario',
      references: 'Referencias',
      review: 'Revisar'
    },

    nav: {
      previous: '← Anterior',
      next: 'Siguiente →',
      submit: 'Enviar Solicitud',
      submitting: 'Enviando...'
    },

    loading: 'Cargando puestos disponibles...',

    noPositions: 'Actualmente no hay puestos disponibles. ¡Vuelva a consultar más tarde!',

    success: {
      title: '¡Solicitud enviada con éxito!',
      body: 'Gracias por su interés. Revisaremos su solicitud y nos pondremos en contacto con usted pronto.',
      button: 'Enviar otra solicitud'
    },

    common: {
      yes: 'Sí',
      no: 'No',
      optional: 'opcional'
    },

    position: {
      title: 'Elija su puesto',
      subtitle: 'Explore los puestos disponibles y seleccione el que desea solicitar',
      searchPlaceholder: 'Buscar puestos por título o ubicación...',
      selected: '✓ Seleccionado',
      viewDescription: '📄 Ver descripción',
      noResults: 'No se encontraron puestos que coincidan con "{{search}}"',
      selectionPrompt: '👆 Seleccione un puesto arriba para continuar',
      previousPage: '← Anterior',
      nextPage: 'Siguiente →',
      pageInfo: 'Página {{page}} de {{totalPages}} ({{count}} puestos)',
      additionalInfo: 'Información adicional',
      howDidYouHear: '¿Cómo se enteró de nosotros?',
      closeModal: 'Cerrar'
    },

    contact: {
      title: 'Información de Contacto',
      sectionTitle: 'Sus Datos',
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      phoneCountryLabel: 'País del teléfono',
      streetAddress: 'Dirección',
      streetAddress2: 'Dirección (línea 2)',
      streetAddress2Placeholder: 'Apartamento, suite, unidad, etc.',
      city: 'Ciudad',
      state: 'Estado',
      selectState: 'Seleccione un estado',
      zip: 'Código postal',
      country: 'País'
    },

    general: {
      title: 'Preguntas Generales',
      workedBefore: '¿Ha trabajado para esta empresa anteriormente?',
      yearsExperience: '¿Cuántos años de experiencia tiene en el puesto que le interesa?',
      atLeast18: '¿Tiene al menos 18 años de edad?',
      under18Notice: 'Tenga en cuenta: los solicitantes menores de 18 años aún pueden presentar su solicitud. Si es seleccionado, deberá proporcionar una autorización por escrito de un padre o tutor legal antes de comenzar a trabajar, y es posible que se requiera documentación adicional.',
      hasFlLicense: '¿Tiene una licencia de conducir o identificación emitida por el estado de Florida?',
      felonyConviction: '¿Ha sido condenado por un delito grave o encarcelado en relación con un delito grave en los últimos siete años?',
      militaryService: '¿Servicio militar?',
      educationLevel: 'Nivel de educación más alto'
    },

    schedule: {
      title: 'Horario',
      employmentType: '¿Prefiere trabajar tiempo completo, medio tiempo o temporal?',
      shiftPreference: '¿Prefiere trabajar de día, de tarde o de noche?',
      availableDays: 'Seleccione los días de la semana en que está disponible para trabajar',
      changingSchedule: '¿Está dispuesto a trabajar con un horario variable?',
      startDate: '¿Cuál es la fecha más próxima en la que puede comenzar?'
    },

    references: {
      title: 'Referencias',
      hasReferences: '¿Tiene alguna referencia?',
      referenceNumber: 'Referencia {{index}}',
      remove: 'Eliminar',
      name: 'Nombre',
      phoneNumber: 'Número de teléfono',
      email: 'Correo electrónico',
      relationship: 'Relación',
      relationshipPlaceholder: 'ej., Antiguo supervisor',
      addAnother: '+ Agregar otra referencia',
      resumeTitle: 'Currículum / CV (opcional)',
      resumeDescription: 'Puede adjuntar un currículum en formato PDF, de hasta 5 MB.',
      attached: 'Adjunto:',
      removeFile: 'Eliminar'
    },

    review: {
      title: 'Revise su solicitud',
      subtitle: 'Por favor revise cuidadosamente su información antes de enviarla.',
      positionSource: 'Puesto y origen',
      position: 'Puesto:',
      howYouHeard: 'Cómo se enteró de nosotros:',
      contactInformation: 'Información de contacto',
      name: 'Nombre:',
      email: 'Correo electrónico:',
      phone: 'Teléfono:',
      address: 'Dirección:',
      generalQuestions: 'Preguntas generales',
      workedHereBefore: 'Trabajó aquí antes:',
      yearsOfExperience: 'Años de experiencia:',
      atLeast18YearsOld: 'Al menos 18 años de edad:',
      floridaLicense: 'Licencia/identificación de Florida:',
      felonyConviction: 'Delito grave (últimos 7 años):',
      militaryService: 'Servicio militar:',
      highestEducation: 'Educación más alta:',
      schedulePreferences: 'Preferencias de horario',
      employmentType: 'Tipo de empleo:',
      shiftPreference: 'Preferencia de turno:',
      availableDays: 'Días disponibles:',
      changingSchedule: 'Dispuesto a trabajar con horario variable:',
      earliestStartDate: 'Fecha de inicio más próxima:',
      references: 'Referencias',
      noReferences: 'No se proporcionaron referencias',
      under18Notice: 'Tenga en cuenta: debido a que indicó que es menor de 18 años, se requerirá autorización por escrito de un padre o tutor legal antes de que pueda comenzar a trabajar si es seleccionado.'
    },

    disclaimer: {
      title: 'Certificación del Solicitante y Aviso de Privacidad',
      certification: 'Certifico que la información contenida en esta solicitud es correcta a mi mejor saber y entender, y que la falsificación deliberada de esta información es motivo de despido. Autorizo a las referencias a proporcionarles toda la información relacionada con mi empleo anterior, ya sea personal o de otro tipo, y libero a todas las partes de toda responsabilidad por cualquier daño que pueda resultar de proporcionarles dicha información. Entiendo que, si soy contratado, seré libre de terminar mi empleo en cualquier momento y por cualquier motivo, y la empresa tiene los mismos derechos. Ningún representante de la empresa tiene autoridad para celebrar ningún acuerdo contrario. Ninguna oferta de empleo es oficial hasta que complete el proceso de contratación.',
      privacyNoticeTitle: 'Aviso de Privacidad para el Solicitante',
      privacyIntro: 'En Clean Tec Outsourcing LLC, nos comprometemos a proteger su privacidad. Por favor revise el siguiente aviso para comprender cómo recopilamos, usamos, compartimos y protegemos su información durante el proceso de solicitud de empleo.',
      privacyPolicyPrefix: 'Para obtener una descripción completa de nuestras prácticas de privacidad, consulte nuestra Política de Privacidad en',
      termsPrefix: 'Consulte nuestros Términos de Servicio en',
      contactPrefix: 'Si tiene alguna pregunta o necesita AYUDA, comuníquese con nosotros en'
    },

    options: {
      source: {
        companyWebsite: 'Sitio web de la empresa',
        indeed: 'Indeed',
        linkedin: 'LinkedIn',
        referral: 'Referencia de un empleado',
        jobFair: 'Feria de empleo',
        other: 'Otro'
      },
      yearsExperience: {
        noExperience: 'Sin experiencia',
        lessThanOneYear: 'Menos de un año',
        oneToThreeYears: 'De 1 a 3 años',
        fourOrMoreYears: '4 años o más'
      },
      educationLevel: {
        highSchool: 'Preparatoria o equivalente (GED)',
        someCollege: 'Estudios universitarios parciales',
        associates: 'Título técnico (Associate)',
        bachelors: 'Licenciatura',
        masters: 'Maestría'
      },
      employmentType: {
        fullTime: 'Tiempo completo',
        partTime: 'Medio tiempo',
        temporary: 'Temporal'
      },
      shiftPreference: {
        days: 'Día',
        afternoons: 'Tarde',
        nights: 'Noche'
      },
      days: {
        all: 'Todos',
        monday: 'Lunes',
        tuesday: 'Martes',
        wednesday: 'Miércoles',
        thursday: 'Jueves',
        friday: 'Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo',
        holidays: 'Días festivos'
      },
      changingSchedule: {
        yes: 'Sí',
        no: 'No',
        occasionally: 'Ocasionalmente'
      },
      startDate: {
        immediately: 'Inmediatamente',
        oneToTwoWeeks: '1-2 semanas',
        overThreeWeeks: 'Más de 3 semanas'
      }
    },

    errors: {
      loadPositionsFailed: 'No se pudieron cargar los puestos. Inténtelo de nuevo más tarde.',
      submitFailed: 'No se pudo enviar la solicitud. Inténtelo de nuevo.',
      generic: 'Algo salió mal. Inténtelo de nuevo.',
      pleaseCorrect: 'Faltan Campos Obligatorios',
      resumeNotPdf: 'Por favor seleccione un archivo PDF.',
      resumeTooLarge: 'Ese archivo pesa más de 5 MB. Por favor seleccione uno más pequeño.',

      fields: {
        position: 'Por favor seleccione un puesto para continuar.',
        firstName: 'Por favor ingrese su nombre.',
        lastName: 'Por favor ingrese su apellido.',
        emailRequired: 'Por favor ingrese su correo electrónico.',
        emailInvalid: 'Por favor ingrese un correo electrónico válido (por ejemplo: nombre@ejemplo.com).',
        phoneRequired: 'Por favor ingrese su número de teléfono.',
        phoneInvalid: 'Por favor ingrese un número de teléfono válido para el país seleccionado.',
        city: 'Por favor ingrese su ciudad.',
        state: 'Por favor seleccione su estado.',
        zip: 'Por favor ingrese su código postal.',
        workedBefore: 'Por favor indique si ha trabajado para esta empresa antes.',
        yearsExperience: 'Por favor seleccione sus años de experiencia.',
        atLeast18: 'Por favor indique si tiene al menos 18 años de edad.',
        hasFlLicense: 'Por favor indique si tiene una licencia o identificación de Florida.',
        felonyConviction: 'Por favor responda la pregunta sobre delitos graves.',
        militaryService: 'Por favor indique su servicio militar.',
        educationLevel: 'Por favor seleccione su nivel de educación más alto.',
        employmentType: 'Por favor seleccione tiempo completo, medio tiempo o temporal.',
        shiftPreference: 'Por favor seleccione su preferencia de turno.',
        availableDays: 'Por favor seleccione al menos un día en el que esté disponible para trabajar.',
        changingSchedule: 'Por favor indique si está dispuesto a trabajar con un horario variable.',
        startDate: 'Por favor seleccione su fecha de inicio más próxima.',
        hasReferences: 'Por favor indique si tiene alguna referencia.'
      },

      references: {
        incomplete: 'Por favor proporcione un nombre y número de teléfono para cada referencia.',
        invalidPhone: 'Por favor ingrese un número de teléfono válido para cada referencia.',
        invalidEmail: 'Por favor ingrese un correo electrónico válido para cada referencia, o déjelo en blanco.'
      }
    },

    serverErrors: {
      required_position: 'Por favor seleccione un puesto para continuar.',
      required_first_name: 'Por favor ingrese su nombre.',
      required_last_name: 'Por favor ingrese su apellido.',
      invalid_email: 'Por favor ingrese un correo electrónico válido (por ejemplo: nombre@ejemplo.com).',
      required_phone: 'Por favor ingrese su número de teléfono.',
      required_phone_country: 'Por favor seleccione el país de su teléfono.',
      invalid_phone_country: 'Por favor seleccione un país válido para su número de teléfono.',
      invalid_phone_for_country: 'Por favor ingrese un número de teléfono válido para el país seleccionado.',
      invalid_references_format: 'Las referencias no tenían el formato correcto.',
      reference_missing_name_or_phone: 'A la referencia {{index}} le falta el nombre o el número de teléfono.',
      invalid_reference_phone_country: 'Por favor seleccione un país válido para el teléfono de la referencia {{index}}.',
      invalid_reference_phone: 'Por favor ingrese un número de teléfono válido para la referencia {{index}}.',
      invalid_reference_email: 'Por favor ingrese un correo electrónico válido para la referencia {{index}}, o déjelo en blanco.',
      position_not_available: 'Este puesto ya no está disponible. Por favor elija otro puesto.',
      resume_too_large: 'El currículum debe pesar 5 MB o menos.',
      resume_invalid: 'No se pudo procesar el archivo subido.',
      too_many_references: 'No se permiten más de {{max}} referencias.'
    }
  },

  // ---------------------------------------------------------------------
  // FRENCH (draft - not yet reviewed)
  // ---------------------------------------------------------------------
  // fr: {
  //   languageSwitcher: {
  //     label: 'Langue'
  //   },

  //   header: {
  //     title: 'Rejoignez Notre Équipe!',
  //     needHelp: 'Aide?',
  //     callUs: '{{phone}}'
  //   },

  //   steps: {
  //     position: 'Poste',
  //     contact: 'Contact',
  //     general: 'Général',
  //     schedule: 'Horaire',
  //     references: 'Références',
  //     review: 'Vérifier'
  //   },

  //   nav: {
  //     previous: '← Précédent',
  //     next: 'Suivant →',
  //     submit: 'Soumettre la candidature',
  //     submitting: 'Envoi en cours...'
  //   },

  //   loading: 'Chargement des postes disponibles...',

  //   noPositions: "Il n'y a actuellement aucun poste ouvert. Veuillez revenir plus tard !",

  //   success: {
  //     title: 'Candidature soumise avec succès !',
  //     body: 'Merci de votre intérêt. Nous examinerons votre candidature et vous contacterons bientôt.',
  //     button: 'Soumettre une autre candidature'
  //   },

  //   common: {
  //     yes: 'Oui',
  //     no: 'Non',
  //     optional: 'facultatif'
  //   },

  //   position: {
  //     title: 'Choisissez votre poste',
  //     subtitle: 'Parcourez les postes disponibles et sélectionnez celui auquel vous souhaitez postuler',
  //     searchPlaceholder: 'Rechercher des postes par titre ou lieu...',
  //     selected: '✓ Sélectionné',
  //     viewDescription: '📄 Voir la description',
  //     noResults: 'Aucun poste trouvé correspondant à "{{search}}"',
  //     selectionPrompt: '👆 Veuillez sélectionner un poste ci-dessus pour continuer',
  //     previousPage: '← Précédent',
  //     nextPage: 'Suivant →',
  //     pageInfo: 'Page {{page}} sur {{totalPages}} ({{count}} postes)',
  //     additionalInfo: 'Informations supplémentaires',
  //     howDidYouHear: 'Comment avez-vous entendu parler de nous ?',
  //     closeModal: 'Fermer'
  //   },

  //   contact: {
  //     title: 'Coordonnées',
  //     sectionTitle: 'Vos informations',
  //     firstName: 'Prénom',
  //     lastName: 'Nom de famille',
  //     email: 'Courriel',
  //     phone: 'Téléphone',
  //     phoneCountryLabel: 'Pays du téléphone',
  //     streetAddress: 'Adresse',
  //     streetAddress2: 'Adresse (ligne 2)',
  //     streetAddress2Placeholder: 'Appartement, suite, unité, etc.',
  //     city: 'Ville',
  //     state: 'État',
  //     selectState: "Sélectionnez l'état",
  //     zip: 'Code postal',
  //     country: 'Pays'
  //   },

  //   general: {
  //     title: 'Questions générales',
  //     workedBefore: 'Avez-vous déjà travaillé pour cette entreprise ?',
  //     yearsExperience: "Combien d'années d'expérience avez-vous dans le poste qui vous intéresse ?",
  //     atLeast18: 'Avez-vous au moins 18 ans ?',
  //     under18Notice: 'Veuillez noter : les candidats de moins de 18 ans peuvent tout de même postuler. Si vous êtes sélectionné(e), vous devrez fournir une autorisation écrite d\'un parent ou tuteur légal avant de pouvoir commencer à travailler, et des documents supplémentaires pourront être exigés.',
  //     hasFlLicense: "Avez-vous un permis de conduire ou une pièce d'identité délivré par l'État de Floride ?",
  //     felonyConviction: 'Avez-vous été condamné(e) pour un crime grave ou incarcéré(e) en lien avec un crime grave au cours des sept dernières années ?',
  //     militaryService: 'Service militaire ?',
  //     educationLevel: "Niveau d'études le plus élevé"
  //   },

  //   schedule: {
  //     title: 'Horaire',
  //     employmentType: 'Préférez-vous travailler à temps plein, à temps partiel ou de façon temporaire ?',
  //     shiftPreference: 'Préférez-vous travailler le jour, l\'après-midi ou la nuit ?',
  //     availableDays: 'Sélectionnez les jours de la semaine où vous êtes disponible pour travailler',
  //     changingSchedule: 'Êtes-vous disposé(e) à travailler selon un horaire variable ?',
  //     startDate: 'Quelle est la date la plus proche à laquelle vous pouvez commencer ?'
  //   },

  //   references: {
  //     title: 'Références',
  //     hasReferences: 'Avez-vous des références ?',
  //     referenceNumber: 'Référence {{index}}',
  //     remove: 'Supprimer',
  //     name: 'Nom',
  //     phoneNumber: 'Numéro de téléphone',
  //     email: 'Courriel',
  //     relationship: 'Relation',
  //     relationshipPlaceholder: 'ex. : ancien superviseur',
  //     addAnother: '+ Ajouter une autre référence',
  //     resumeTitle: 'CV (facultatif)',
  //     resumeDescription: 'Vous pouvez joindre un CV au format PDF, jusqu\'à 5 Mo.',
  //     attached: 'Joint :',
  //     removeFile: 'Supprimer'
  //   },

  //   review: {
  //     title: 'Vérifiez votre candidature',
  //     subtitle: 'Veuillez vérifier attentivement vos informations avant de les soumettre.',
  //     positionSource: 'Poste et provenance',
  //     position: 'Poste :',
  //     howYouHeard: 'Comment vous avez entendu parler de nous :',
  //     contactInformation: 'Coordonnées',
  //     name: 'Nom :',
  //     email: 'Courriel :',
  //     phone: 'Téléphone :',
  //     address: 'Adresse :',
  //     generalQuestions: 'Questions générales',
  //     workedHereBefore: 'A déjà travaillé ici :',
  //     yearsOfExperience: "Années d'expérience :",
  //     atLeast18YearsOld: 'Au moins 18 ans :',
  //     floridaLicense: "Permis/pièce d'identité de Floride :",
  //     felonyConviction: 'Crime grave (7 dernières années) :',
  //     militaryService: 'Service militaire :',
  //     highestEducation: "Niveau d'études le plus élevé :",
  //     schedulePreferences: "Préférences d'horaire",
  //     employmentType: "Type d'emploi :",
  //     shiftPreference: 'Préférence de quart :',
  //     availableDays: 'Jours disponibles :',
  //     changingSchedule: 'Disposé(e) à un horaire variable :',
  //     earliestStartDate: 'Date de début la plus proche :',
  //     references: 'Références',
  //     noReferences: 'Aucune référence fournie',
  //     under18Notice: 'Veuillez noter : comme vous avez indiqué avoir moins de 18 ans, une autorisation écrite d\'un parent ou tuteur légal sera requise avant que vous puissiez commencer à travailler si vous êtes sélectionné(e).'
  //   },

  //   disclaimer: {
  //     title: 'Certification du candidat et avis de confidentialité',
  //     certification: "Je certifie que les informations contenues dans cette demande sont exactes au meilleur de ma connaissance, et que toute falsification délibérée de ces informations constitue un motif de renvoi. J'autorise les référents à vous communiquer toute information concernant mon emploi antérieur, personnelle ou autre, et je dégage toutes les parties de toute responsabilité pour tout dommage pouvant résulter de la communication de ces informations. Je comprends que si je suis embauché(e), je serai libre de mettre fin à mon emploi à tout moment et pour quelque raison que ce soit, et que l'entreprise dispose des mêmes droits. Aucun représentant de l'entreprise n'a l'autorité de conclure un accord contraire. Aucune offre d'emploi n'est officielle tant que je n'ai pas terminé le processus d'embauche.",
  //     privacyNoticeTitle: 'Avis de confidentialité du candidat',
  //     privacyIntro: "Chez Clean Tec Outsourcing LLC, nous nous engageons à protéger votre vie privée. Veuillez consulter l'avis suivant pour comprendre comment nous collectons, utilisons, partageons et sécurisons vos informations tout au long du processus de candidature.",
  //     privacyPolicyPrefix: 'Pour une description complète de nos pratiques de confidentialité, consultez notre politique de confidentialité à',
  //     termsPrefix: 'Consultez nos conditions d\'utilisation à',
  //     contactPrefix: "Si vous avez des questions ou besoin d'AIDE, veuillez nous contacter à"
  //   },

  //   options: {
  //     source: {
  //       companyWebsite: "Site web de l'entreprise",
  //       indeed: 'Indeed',
  //       linkedin: 'LinkedIn',
  //       referral: "Recommandation d'un employé",
  //       jobFair: "Salon de l'emploi",
  //       other: 'Autre'
  //     },
  //     yearsExperience: {
  //       noExperience: "Aucune expérience",
  //       lessThanOneYear: "Moins d'un an",
  //       oneToThreeYears: '1 à 3 ans',
  //       fourOrMoreYears: '4 ans ou plus'
  //     },
  //     educationLevel: {
  //       highSchool: "Diplôme d'études secondaires ou équivalent (GED)",
  //       someCollege: 'Études universitaires partielles',
  //       associates: 'Diplôme technique (Associate)',
  //       bachelors: 'Licence',
  //       masters: 'Maîtrise'
  //     },
  //     employmentType: {
  //       fullTime: 'Temps plein',
  //       partTime: 'Temps partiel',
  //       temporary: 'Temporaire'
  //     },
  //     shiftPreference: {
  //       days: 'Jour',
  //       afternoons: 'Après-midi',
  //       nights: 'Nuit'
  //     },
  //     days: {
  //       all: 'Tous',
  //       monday: 'Lundi',
  //       tuesday: 'Mardi',
  //       wednesday: 'Mercredi',
  //       thursday: 'Jeudi',
  //       friday: 'Vendredi',
  //       saturday: 'Samedi',
  //       sunday: 'Dimanche',
  //       holidays: 'Jours fériés'
  //     },
  //     changingSchedule: {
  //       yes: 'Oui',
  //       no: 'Non',
  //       occasionally: 'Occasionnellement'
  //     },
  //     startDate: {
  //       immediately: 'Immédiatement',
  //       oneToTwoWeeks: '1-2 semaines',
  //       overThreeWeeks: 'Plus de 3 semaines'
  //     }
  //   },

  //   errors: {
  //     loadPositionsFailed: 'Impossible de charger les postes. Veuillez réessayer plus tard.',
  //     submitFailed: "Échec de l'envoi de la candidature. Veuillez réessayer.",
  //     generic: "Une erreur s'est produite. Veuillez réessayer.",
  //     pleaseCorrect: 'Champs Obligatoires Manquants',
  //     resumeNotPdf: 'Veuillez choisir un fichier PDF.',
  //     resumeTooLarge: 'Ce fichier dépasse 5 Mo. Veuillez choisir un fichier plus petit.',

  //     fields: {
  //       position: 'Veuillez sélectionner un poste pour continuer.',
  //       firstName: 'Veuillez saisir votre prénom.',
  //       lastName: 'Veuillez saisir votre nom de famille.',
  //       emailRequired: 'Veuillez saisir votre adresse courriel.',
  //       emailInvalid: 'Veuillez saisir une adresse courriel valide (par exemple : nom@exemple.com).',
  //       phoneRequired: 'Veuillez saisir votre numéro de téléphone.',
  //       phoneInvalid: 'Veuillez saisir un numéro de téléphone valide pour le pays sélectionné.',
  //       city: 'Veuillez saisir votre ville.',
  //       state: 'Veuillez sélectionner votre état.',
  //       zip: 'Veuillez saisir votre code postal.',
  //       workedBefore: 'Veuillez indiquer si vous avez déjà travaillé pour cette entreprise.',
  //       yearsExperience: "Veuillez sélectionner vos années d'expérience.",
  //       atLeast18: 'Veuillez indiquer si vous avez au moins 18 ans.',
  //       hasFlLicense: "Veuillez indiquer si vous avez un permis ou une pièce d'identité de Floride.",
  //       felonyConviction: 'Veuillez répondre à la question sur les crimes graves.',
  //       militaryService: 'Veuillez indiquer votre service militaire.',
  //       educationLevel: "Veuillez sélectionner votre niveau d'études le plus élevé.",
  //       employmentType: 'Veuillez sélectionner temps plein, temps partiel ou temporaire.',
  //       shiftPreference: 'Veuillez sélectionner votre préférence de quart.',
  //       availableDays: 'Veuillez sélectionner au moins un jour où vous êtes disponible pour travailler.',
  //       changingSchedule: 'Veuillez indiquer si vous êtes disposé(e) à travailler selon un horaire variable.',
  //       startDate: 'Veuillez sélectionner votre date de début la plus proche.',
  //       hasReferences: 'Veuillez indiquer si vous avez des références.'
  //     },

  //     references: {
  //       incomplete: 'Veuillez fournir un nom et un numéro de téléphone pour chaque référence.',
  //       invalidPhone: 'Veuillez saisir un numéro de téléphone valide pour chaque référence.',
  //       invalidEmail: 'Veuillez saisir une adresse courriel valide pour chaque référence, ou la laisser vide.'
  //     }
  //   },

  //   serverErrors: {
  //     required_position: 'Veuillez sélectionner un poste pour continuer.',
  //     required_first_name: 'Veuillez saisir votre prénom.',
  //     required_last_name: 'Veuillez saisir votre nom de famille.',
  //     invalid_email: 'Veuillez saisir une adresse courriel valide (par exemple : nom@exemple.com).',
  //     required_phone: 'Veuillez saisir votre numéro de téléphone.',
  //     required_phone_country: 'Veuillez sélectionner le pays de votre téléphone.',
  //     invalid_phone_country: 'Veuillez sélectionner un pays valide pour votre numéro de téléphone.',
  //     invalid_phone_for_country: 'Veuillez saisir un numéro de téléphone valide pour le pays sélectionné.',
  //     invalid_references_format: "Les références n'étaient pas correctement formatées.",
  //     reference_missing_name_or_phone: 'Il manque un nom ou un numéro de téléphone pour la référence {{index}}.',
  //     invalid_reference_phone_country: 'Veuillez sélectionner un pays valide pour le téléphone de la référence {{index}}.',
  //     invalid_reference_phone: 'Veuillez saisir un numéro de téléphone valide pour la référence {{index}}.',
  //     invalid_reference_email: 'Veuillez saisir une adresse courriel valide pour la référence {{index}}, ou la laisser vide.',
  //     position_not_available: "Ce poste n'est plus disponible. Veuillez choisir un autre poste.",
  //     resume_too_large: 'Le CV doit peser 5 Mo ou moins.',
  //     resume_invalid: "Impossible de traiter le fichier téléversé.",
  //     too_many_references: 'Pas plus de {{max}} références ne sont autorisées.'
  //   }
  // },

  // ---------------------------------------------------------------------
  // PORTUGUESE (draft - not yet reviewed)
  // ---------------------------------------------------------------------
  // pt: {
  //   languageSwitcher: {
  //     label: 'Idioma'
  //   },

  //   header: {
  //     title: 'Junte-se à Nossa Equipe!',
  //     needHelp: 'Ajuda?',
  //     callUs: '{{phone}}'
  //   },

  //   steps: {
  //     position: 'Cargo',
  //     contact: 'Contato',
  //     general: 'Geral',
  //     schedule: 'Horário',
  //     references: 'Referências',
  //     review: 'Revisar'
  //   },

  //   nav: {
  //     previous: '← Anterior',
  //     next: 'Próximo →',
  //     submit: 'Enviar Candidatura',
  //     submitting: 'Enviando...'
  //   },

  //   loading: 'Carregando vagas disponíveis...',

  //   noPositions: 'No momento não há vagas abertas. Volte a verificar mais tarde!',

  //   success: {
  //     title: 'Candidatura enviada com sucesso!',
  //     body: 'Obrigado pelo seu interesse. Analisaremos sua candidatura e entraremos em contato em breve.',
  //     button: 'Enviar outra candidatura'
  //   },

  //   common: {
  //     yes: 'Sim',
  //     no: 'Não',
  //     optional: 'opcional'
  //   },

  //   position: {
  //     title: 'Escolha seu cargo',
  //     subtitle: 'Explore as vagas disponíveis e selecione aquela para a qual deseja se candidatar',
  //     searchPlaceholder: 'Buscar vagas por título ou localização...',
  //     selected: '✓ Selecionado',
  //     viewDescription: '📄 Ver descrição',
  //     noResults: 'Nenhuma vaga encontrada correspondente a "{{search}}"',
  //     selectionPrompt: '👆 Selecione uma vaga acima para continuar',
  //     previousPage: '← Anterior',
  //     nextPage: 'Próximo →',
  //     pageInfo: 'Página {{page}} de {{totalPages}} ({{count}} vagas)',
  //     additionalInfo: 'Informações adicionais',
  //     howDidYouHear: 'Como você soube de nós?',
  //     closeModal: 'Fechar'
  //   },

  //   contact: {
  //     title: 'Informações de Contato',
  //     sectionTitle: 'Seus Dados',
  //     firstName: 'Nome',
  //     lastName: 'Sobrenome',
  //     email: 'E-mail',
  //     phone: 'Telefone',
  //     phoneCountryLabel: 'País do telefone',
  //     streetAddress: 'Endereço',
  //     streetAddress2: 'Endereço (linha 2)',
  //     streetAddress2Placeholder: 'Apartamento, suíte, unidade, etc.',
  //     city: 'Cidade',
  //     state: 'Estado',
  //     selectState: 'Selecione o estado',
  //     zip: 'Código postal (ZIP)',
  //     country: 'País'
  //   },

  //   general: {
  //     title: 'Perguntas Gerais',
  //     workedBefore: 'Você já trabalhou para esta empresa antes?',
  //     yearsExperience: 'Quantos anos de experiência você tem na função pela qual está interessado(a)?',
  //     atLeast18: 'Você tem pelo menos 18 anos de idade?',
  //     under18Notice: 'Observação: candidatos menores de 18 anos ainda podem se candidatar. Se selecionado(a), será necessário fornecer autorização por escrito de um dos pais ou responsável legal antes de começar a trabalhar, e documentação adicional poderá ser exigida.',
  //     hasFlLicense: 'Você possui carteira de motorista ou identidade emitida pelo estado da Flórida?',
  //     felonyConviction: 'Você foi condenado(a) por um crime grave ou esteve preso(a) em relação a um crime grave nos últimos sete anos?',
  //     militaryService: 'Serviço militar?',
  //     educationLevel: 'Nível de escolaridade mais alto'
  //   },

  //   schedule: {
  //     title: 'Horário',
  //     employmentType: 'Você prefere trabalhar em período integral, meio período ou temporário?',
  //     shiftPreference: 'Você prefere trabalhar de dia, à tarde ou à noite?',
  //     availableDays: 'Selecione os dias da semana em que está disponível para trabalhar',
  //     changingSchedule: 'Você está disposto(a) a trabalhar com um horário variável?',
  //     startDate: 'Qual é a data mais próxima em que você pode começar?'
  //   },

  //   references: {
  //     title: 'Referências',
  //     hasReferences: 'Você tem alguma referência?',
  //     referenceNumber: 'Referência {{index}}',
  //     remove: 'Remover',
  //     name: 'Nome',
  //     phoneNumber: 'Número de telefone',
  //     email: 'E-mail',
  //     relationship: 'Relação',
  //     relationshipPlaceholder: 'ex.: ex-supervisor',
  //     addAnother: '+ Adicionar outra referência',
  //     resumeTitle: 'Currículo (opcional)',
  //     resumeDescription: 'Você pode anexar um currículo em PDF, com até 5 MB.',
  //     attached: 'Anexado:',
  //     removeFile: 'Remover'
  //   },

  //   review: {
  //     title: 'Revise sua candidatura',
  //     subtitle: 'Por favor, revise cuidadosamente suas informações antes de enviar.',
  //     positionSource: 'Cargo e origem',
  //     position: 'Cargo:',
  //     howYouHeard: 'Como você soube de nós:',
  //     contactInformation: 'Informações de contato',
  //     name: 'Nome:',
  //     email: 'E-mail:',
  //     phone: 'Telefone:',
  //     address: 'Endereço:',
  //     generalQuestions: 'Perguntas gerais',
  //     workedHereBefore: 'Trabalhou aqui antes:',
  //     yearsOfExperience: 'Anos de experiência:',
  //     atLeast18YearsOld: 'Pelo menos 18 anos de idade:',
  //     floridaLicense: 'Carteira/identidade da Flórida:',
  //     felonyConviction: 'Crime grave (últimos 7 anos):',
  //     militaryService: 'Serviço militar:',
  //     highestEducation: 'Escolaridade mais alta:',
  //     schedulePreferences: 'Preferências de horário',
  //     employmentType: 'Tipo de emprego:',
  //     shiftPreference: 'Preferência de turno:',
  //     availableDays: 'Dias disponíveis:',
  //     changingSchedule: 'Disposto(a) a trabalhar com horário variável:',
  //     earliestStartDate: 'Data de início mais próxima:',
  //     references: 'Referências',
  //     noReferences: 'Nenhuma referência fornecida',
  //     under18Notice: 'Observação: como você indicou ter menos de 18 anos, será necessária autorização por escrito de um dos pais ou responsável legal antes de começar a trabalhar, caso seja selecionado(a).'
  //   },

  //   disclaimer: {
  //     title: 'Certificação do Candidato e Aviso de Privacidade',
  //     certification: 'Certifico que as informações contidas nesta solicitação estão corretas, até onde eu saiba, e que a falsificação deliberada dessas informações é motivo para demissão. Autorizo as referências a fornecerem todas e quaisquer informações sobre meu emprego anterior, pessoais ou não, e isento todas as partes de qualquer responsabilidade por danos que possam resultar do fornecimento dessas informações. Entendo que, se for contratado(a), serei livre para encerrar meu emprego a qualquer momento e por qualquer motivo, e que a empresa tem os mesmos direitos. Nenhum representante da empresa tem autoridade para fazer qualquer acordo contrário. Nenhuma oferta de emprego é oficial até que eu complete o processo de admissão.',
  //     privacyNoticeTitle: 'Aviso de Privacidade do Candidato',
  //     privacyIntro: 'Na Clean Tec Outsourcing LLC, estamos comprometidos em proteger sua privacidade. Por favor, revise o aviso a seguir para entender como coletamos, usamos, compartilhamos e protegemos suas informações durante o processo de candidatura.',
  //     privacyPolicyPrefix: 'Para uma descrição completa de nossas práticas de privacidade, consulte nossa Política de Privacidade em',
  //     termsPrefix: 'Veja nossos Termos de Serviço em',
  //     contactPrefix: 'Se tiver alguma dúvida ou precisar de AJUDA, entre em contato conosco em'
  //   },

  //   options: {
  //     source: {
  //       companyWebsite: 'Site da empresa',
  //       indeed: 'Indeed',
  //       linkedin: 'LinkedIn',
  //       referral: 'Indicação de funcionário',
  //       jobFair: 'Feira de empregos',
  //       other: 'Outro'
  //     },
  //     yearsExperience: {
  //       noExperience: 'Sem experiência',
  //       lessThanOneYear: 'Menos de um ano',
  //       oneToThreeYears: 'De 1 a 3 anos',
  //       fourOrMoreYears: '4 anos ou mais'
  //     },
  //     educationLevel: {
  //       highSchool: 'Ensino médio ou equivalente (GED)',
  //       someCollege: 'Ensino superior incompleto',
  //       associates: 'Curso técnico (Associate)',
  //       bachelors: 'Graduação (Bacharelado)',
  //       masters: 'Mestrado'
  //     },
  //     employmentType: {
  //       fullTime: 'Período integral',
  //       partTime: 'Meio período',
  //       temporary: 'Temporário'
  //     },
  //     shiftPreference: {
  //       days: 'Dia',
  //       afternoons: 'Tarde',
  //       nights: 'Noite'
  //     },
  //     days: {
  //       all: 'Todos',
  //       monday: 'Segunda-feira',
  //       tuesday: 'Terça-feira',
  //       wednesday: 'Quarta-feira',
  //       thursday: 'Quinta-feira',
  //       friday: 'Sexta-feira',
  //       saturday: 'Sábado',
  //       sunday: 'Domingo',
  //       holidays: 'Feriados'
  //     },
  //     changingSchedule: {
  //       yes: 'Sim',
  //       no: 'Não',
  //       occasionally: 'Ocasionalmente'
  //     },
  //     startDate: {
  //       immediately: 'Imediatamente',
  //       oneToTwoWeeks: '1-2 semanas',
  //       overThreeWeeks: 'Mais de 3 semanas'
  //     }
  //   },

  //   errors: {
  //     loadPositionsFailed: 'Não foi possível carregar as vagas. Tente novamente mais tarde.',
  //     submitFailed: 'Falha ao enviar a candidatura. Tente novamente.',
  //     generic: 'Algo deu errado. Tente novamente.',
  //     pleaseCorrect: 'Campos Obrigatórios Faltando',
  //     resumeNotPdf: 'Por favor, escolha um arquivo PDF.',
  //     resumeTooLarge: 'Esse arquivo tem mais de 5 MB. Por favor, escolha um arquivo menor.',

  //     fields: {
  //       position: 'Por favor, selecione um cargo para continuar.',
  //       firstName: 'Por favor, digite seu nome.',
  //       lastName: 'Por favor, digite seu sobrenome.',
  //       emailRequired: 'Por favor, digite seu e-mail.',
  //       emailInvalid: 'Por favor, digite um e-mail válido (por exemplo: nome@exemplo.com).',
  //       phoneRequired: 'Por favor, digite seu número de telefone.',
  //       phoneInvalid: 'Por favor, digite um número de telefone válido para o país selecionado.',
  //       city: 'Por favor, digite sua cidade.',
  //       state: 'Por favor, selecione seu estado.',
  //       zip: 'Por favor, digite seu código postal.',
  //       workedBefore: 'Por favor, informe se já trabalhou para esta empresa antes.',
  //       yearsExperience: 'Por favor, selecione seus anos de experiência.',
  //       atLeast18: 'Por favor, informe se tem pelo menos 18 anos de idade.',
  //       hasFlLicense: 'Por favor, informe se possui carteira ou identidade da Flórida.',
  //       felonyConviction: 'Por favor, responda à pergunta sobre crimes graves.',
  //       militaryService: 'Por favor, informe sobre seu serviço militar.',
  //       educationLevel: 'Por favor, selecione seu nível de escolaridade mais alto.',
  //       employmentType: 'Por favor, selecione período integral, meio período ou temporário.',
  //       shiftPreference: 'Por favor, selecione sua preferência de turno.',
  //       availableDays: 'Por favor, selecione pelo menos um dia em que esteja disponível para trabalhar.',
  //       changingSchedule: 'Por favor, informe se está disposto(a) a trabalhar com horário variável.',
  //       startDate: 'Por favor, selecione sua data de início mais próxima.',
  //       hasReferences: 'Por favor, informe se tem alguma referência.'
  //     },

  //     references: {
  //       incomplete: 'Por favor, forneça um nome e número de telefone para cada referência.',
  //       invalidPhone: 'Por favor, digite um número de telefone válido para cada referência.',
  //       invalidEmail: 'Por favor, digite um e-mail válido para cada referência, ou deixe em branco.'
  //     }
  //   },

  //   serverErrors: {
  //     required_position: 'Por favor, selecione um cargo para continuar.',
  //     required_first_name: 'Por favor, digite seu nome.',
  //     required_last_name: 'Por favor, digite seu sobrenome.',
  //     invalid_email: 'Por favor, digite um e-mail válido (por exemplo: nome@exemplo.com).',
  //     required_phone: 'Por favor, digite seu número de telefone.',
  //     required_phone_country: 'Por favor, selecione o país do seu telefone.',
  //     invalid_phone_country: 'Por favor, selecione um país válido para o seu número de telefone.',
  //     invalid_phone_for_country: 'Por favor, digite um número de telefone válido para o país selecionado.',
  //     invalid_references_format: 'As referências não estavam formatadas corretamente.',
  //     reference_missing_name_or_phone: 'A referência {{index}} está sem nome ou número de telefone.',
  //     invalid_reference_phone_country: 'Por favor, selecione um país válido para o telefone da referência {{index}}.',
  //     invalid_reference_phone: 'Por favor, digite um número de telefone válido para a referência {{index}}.',
  //     invalid_reference_email: 'Por favor, digite um e-mail válido para a referência {{index}}, ou deixe em branco.',
  //     position_not_available: 'Este cargo não está mais disponível. Por favor, escolha outro cargo.',
  //     resume_too_large: 'O currículo deve ter 5 MB ou menos.',
  //     resume_invalid: 'Não foi possível processar o arquivo enviado.',
  //     too_many_references: 'Não são permitidas mais de {{max}} referências.'
  //   }
  // }
};

export const SUPPORTED_LANGUAGE_CODES = Object.keys(translations);
